import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { webOrigin } from "@/lib/nativeBridge";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CopyIcon as Copy,
  PaperPlaneTiltIcon as PaperPlaneTilt,
  UsersThreeIcon as UsersThree,
  AppleLogoIcon as AppleLogo,
  GooglePlayLogoIcon as GooglePlayLogo,
} from "@phosphor-icons/react";

const IOS_URL = "https://apps.apple.com/us/app/armstrong-k9-registry/id6790544132";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.armstrongk9registry";

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Couldn't copy — long-press to select instead");
  }
};

export default function InviteFriends() {
  const [refCode, setRefCode] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.post("/breeder-onboarding/referral-link").then((r) => setRefCode(r.data?.code || null)).catch(() => {});
    api.get("/breeder-onboarding/referral-link/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const refUrl = refCode ? `${webOrigin()}/register?ref=${refCode}` : `${webOrigin()}/register`;
  const scripts = [
    {
      key: "dog-friend",
      label: "For a dog-loving friend",
      text: `Hey! I'm on Armstrong K9 Registry — it's like a home base for dog people: a dog social feed, care tracking, events, and vetted groomers/trainers you can book online. Free to join, come find me on there: ${refUrl}`,
    },
    {
      key: "breeder-friend",
      label: "For a breeder friend",
      text: `You'd love Armstrong K9 Registry — real pedigrees, litter listings, live video tours with buyers (you keep up to 80%), and escrow-protected deposits. Plans from $19/mo. Join here: ${refUrl}`,
    },
    {
      key: "puppy-friend",
      label: "For someone puppy hunting",
      text: `If you're looking for a puppy — Armstrong K9 Registry has verified breeders, real pedigrees, live video tours before you commit, and escrow-protected deposits. Free to browse: ${refUrl}`,
    },
  ];

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="invite-friends-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">Spread the word</p>
        <h1 className="font-spectral text-4xl tracking-tight">Invite friends</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
          Every dog person you bring makes the registry better — more kennels, more litters, more
          pros, more fun. Signups through your link are credited to you.
        </p>

        <div className="mt-6 bg-ink text-white rounded-xl border border-gold/40 p-5" data-testid="invite-link-card">
          <p className="text-[11px] font-semibold text-gold uppercase tracking-[0.18em] mb-2">Your invite link</p>
          <div className="flex flex-wrap items-center gap-3">
            <code className="text-[13px] bg-white/10 rounded-lg px-3 py-1.5 break-all" data-testid="invite-ref-url">{refUrl}</code>
            <Button size="sm" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white text-xs"
              onClick={() => copyText(refUrl, "Invite link")} data-testid="invite-copy-link-btn">
              <Copy size={12} className="mr-1" /> Copy
            </Button>
          </div>
          {stats && stats.uses > 0 && (
            <p className="mt-3 pt-3 border-t border-white/15 text-[12.5px] text-white/80 inline-flex items-center gap-1.5" data-testid="invite-stats">
              <UsersThree size={14} weight="duotone" className="text-gold" />
              {stats.uses} {stats.uses === 1 ? "member has" : "members have"} joined through you — thank you!
            </p>
          )}
        </div>

        <div className="mt-6 space-y-4">
          {scripts.map((s) => (
            <div key={s.key} className="bg-white border border-[#EAEAEA] rounded-xl p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid={`invite-script-${s.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-spectral text-lg">{s.label}</h2>
                <Button size="sm" variant="outline" className="rounded-full text-xs"
                  onClick={() => copyText(s.text, "Message")} data-testid={`invite-copy-${s.key}`}>
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#333] bg-[#FBFAF7] border border-[#EFEBE0] rounded-lg p-3.5">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 text-[12.5px] text-[#6B6B6B]" data-testid="invite-app-links">
          <button onClick={() => copyText(IOS_URL, "App Store link")} className="inline-flex items-center gap-1.5 hover:text-ink transition-colors" data-testid="invite-copy-ios">
            <AppleLogo size={14} weight="fill" /> Copy App Store link
          </button>
          <button onClick={() => copyText(ANDROID_URL, "Google Play link")} className="inline-flex items-center gap-1.5 hover:text-ink transition-colors" data-testid="invite-copy-android">
            <GooglePlayLogo size={14} weight="fill" /> Copy Google Play link
          </button>
          {navigator.share && (
            <button
              onClick={() => navigator.share({ title: "Armstrong K9 Registry", url: refUrl }).catch(() => {})}
              className="inline-flex items-center gap-1.5 text-crimson font-semibold hover:underline"
              data-testid="invite-native-share"
            >
              <PaperPlaneTilt size={14} weight="duotone" /> Share…
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
